import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import AuthContext from 'contexts/AuthContext'

import {
  Archive as ArchiveIcon,
  BarChart as BarChartIcon,
  Briefcase as BriefcaseIcon,
  DollarSign as DollarIcon,
  Layers as LayersIcon,
  Settings as SettingsIcon,
  User as UserIcon,
  Users as UsersIcon,
  Upload as UploadIcon,
  HelpCircle as HelpIcon,
  Feather as FeatherIcon,
  Code as CodeIcon,
  Clipboard as BoxIcon,
  TrendingDown as TrendingDownIcon,
} from 'react-feather'

import * as service from 'service'
import permissionsTags from 'constants/permissions'

import helpers from 'helpers'
import { routes } from 'Routes'
import constants from 'constants/index'

const AuthProvider = ({ children }) => {
  const [loaded, setLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [user, setUser] = useState(null)
  const [permissions, setPermissions] = useState(null)
  const [menuItems, setMenuItems] = useState([])
  const [auth, setAuth] = useState(false)
  const [homePage, setHomePage] = useState()

  const queryClient = useQueryClient()

  const loadData = async () => {
    const responseMe = await service.dponet.user.me()
    const responsePermissions = await service.backoffice.users.permissions()

    const permissions = responsePermissions?.data?.profilePermissions
    const user = responseMe?.data?.user

    setUser(user)
    setPermissions(permissions)

    if (user?.profile?.id === constants.user.ADMIN_PROFILE_ID) {
      setHomePage(routes.dataProcess.all)
    } else {
      loadHomePage(permissions)
    }

    const menuItems = [
      {
        somePermissions: [
          permissionsTags.COMPANIES.LIST,
          permissionsTags.DEPARTMENT.LIST,
          permissionsTags.QUESTIONNAIRE_BY_COMPANY.LIST,
          permissionsTags.COMPANY_SUPPLIERS.LIST,
        ],
        items: [
          {
            main: true,
            title: 'Empresas',
            icon: BriefcaseIcon,
            items: [
              {
                title: 'Empresas',
                permission: permissionsTags.COMPANIES.LIST,
                href: '/companies',
              },
              {
                title: 'Departamentos',
                permission: permissionsTags.DEPARTMENT.LIST,
                href: routes.departments.all,
              },
              {
                title: 'Questionários',
                permission: permissionsTags.QUESTIONNAIRE_BY_COMPANY.LIST,
                href: '/questionnaires',
              },
              {
                title: 'Fornecedores',
                permission: permissionsTags.COMPANY_SUPPLIERS.LIST,
                href: routes.suppliers.all,
              },
              {
                title: 'DPO Easy',
                permission: permissionsTags.COMPANIES.LIST,
                href: '/companies/lite',
              },
            ],
          },
        ],
      },
      {
        somePermissions: [
          permissionsTags.ORGANIZATION.VIEW,
          permissionsTags.ORGANIZATION.MANAGE,
        ],
        items: [
          {
            main: true,
            title: 'Grupos Advisor',
            permission: permissionsTags.ORGANIZATION.VIEW,
            icon: BoxIcon,
            href: '/organizations',
          },
        ],
      },
      {
        items: [
          {
            main: true,
            title: 'Processos',
            permission: permissionsTags.DATA_PROCESSES.LIST,
            icon: LayersIcon,
            href: '/data-processes/',
          },
        ],
      },
      {
        somePermissions: [
          permissionsTags.USERS.LIST,
          permissionsTags.COLLABORATORS.LIST,
        ],
        items: [
          {
            main: true,
            title: 'Pessoas',
            icon: UsersIcon,
            items: [
              {
                title: 'Usuários',
                permission: permissionsTags.USERS.LIST,
                href: '/users',
              },
              {
                title: 'Colaboradores',
                permission: permissionsTags.COLLABORATORS.LIST,
                href: '/collaborators',
              },
            ],
          },
        ],
      },
      {
        somePermissions: [
          permissionsTags.ADVISOR_PROFILE.VIEW,
          permissionsTags.CLIENT_PROFILES.LIST,
          permissionsTags.COLLABORATOR_PROFILES.LIST,
        ],
        items: [
          {
            main: true,
            title: 'Perfis',
            icon: UserIcon,
            items: [
              {
                title: 'Perfis de usuário',
                permission: permissionsTags.CLIENT_PROFILES.LIST,
                href: '/user-profiles',
              },
              {
                title: 'Perfis Advisor',
                permission: permissionsTags.ADVISOR_PROFILE.VIEW,
                href: '/user-profiles/organizations',
              },
              {
                title: 'Perfis de colaborador',
                permission: permissionsTags.COLLABORATOR_PROFILES.LIST,
                href: '/collaborator-profiles',
              },
            ],
          },
        ],
      },
      {
        somePermissions: [
          permissionsTags.PLANS.LIST,
          permissionsTags.COUPONS.LIST,
          permissionsTags.PRE_REGISTRATIONS.LIST,
          permissionsTags.PAYMENT_CONTRACTS.LIST,
          permissionsTags.PAYMENT_ORDERS.LIST,
          permissionsTags.SELLERS.LIST,
          permissionsTags.PAYMENT_SERVICE_TEMPLATES.LIST,
        ],
        items: [
          {
            main: true,
            title: 'Vendas',
            icon: DollarIcon,
            items: [
              {
                title: 'Clientes',
                somePermissions: [permissionsTags.PRE_REGISTRATIONS.LIST],
                href: routes.preRegistrations.all,
              },
              {
                title: 'Contratos',
                somePermissions: [permissionsTags.PAYMENT_CONTRACTS.LIST],
                href: routes.contracts.all,
              },
              {
                title: 'Ordens de pagamentos',
                somePermissions: [permissionsTags.PAYMENT_ORDERS.LIST],
                href: routes.paymentOrders.all,
              },
              {
                title: 'Tipos de serviços',
                somePermissions: [
                  permissionsTags.PAYMENT_SERVICE_TEMPLATES.LIST,
                ],
                href: routes.paymentServiceTemplates.all,
              },
              {
                title: 'Vendedor & Parceiro',
                href: routes.sellers.all,
                somePermissions: [permissionsTags.SELLERS.LIST],
              },
              {
                title: 'Sublicenciamentos',
                somePermissions: [permissionsTags.PRE_REGISTRATIONS.LIST],
                href: routes.sublicensings.all,
              },
              {
                title: 'Planos',
                somePermissions: [permissionsTags.PLANS.LIST],
                href: routes.plans.all,
              },
              {
                title: 'Cupons',
                permission: permissionsTags.COUPONS.LIST,
                href: routes.coupons.all,
              },
              {
                title: 'Dashboard financeiro',
                href: routes.financialDashboard.all,
              },
            ],
          },
        ],
      },
      {
        somePermissions: [
          permissionsTags.CHURNS.LIST,
          permissionsTags.CHURNS.CANCEL,
          permissionsTags.CHURNS.CREATE,
          permissionsTags.CHURNS.DESTROY,
          permissionsTags.CHURNS.SHOW,
          permissionsTags.CHURNS.UPDATE,
          // vai vir outras permissoes
        ],
        items: [
          {
            main: true,
            title: 'Churns',
            icon: TrendingDownIcon,
            items: [
              {
                title: 'Solicitações',
                somePermissions: [
                  permissionsTags.CHURNS.LIST,
                  permissionsTags.CHURNS.CANCEL,
                  permissionsTags.CHURNS.CREATE,
                  permissionsTags.CHURNS.DESTROY,
                  permissionsTags.CHURNS.SHOW,
                  permissionsTags.CHURNS.UPDATE,
                ],
                href: routes.churns.all,
              },
              {
                title: 'Motivos',
                somePermissions: [
                  permissionsTags.CHURN_REASONS.LIST,
                  permissionsTags.CHURN_REASONS.CREATE,
                  permissionsTags.CHURN_REASONS.UPDATE,
                  permissionsTags.CHURN_REASONS.DESTROY,
                ],
                href: routes.churnReasons.all,
              },
            ],
          },
        ],
      },
      {
        items: [
          {
            somePermissions: [
              permissionsTags.SUBGROUPS.LIST,
              permissionsTags.GROUPS.LIST,
              permissionsTags.SEGMENTS.LIST,
              permissionsTags.ECONOMIC_ACTIVITIES.LIST,
              permissionsTags.COMPANY_SIZES.LIST,
            ],
            main: true,
            title: 'Segmentação',
            icon: ArchiveIcon,
            items: [
              {
                title: 'Grupos',
                href: '/groups',
                permission: permissionsTags.GROUPS.LIST,
              },
              {
                title: 'Subgrupos',
                href: '/subgroups',
                permission: permissionsTags.SUBGROUPS.LIST,
              },
              {
                title: 'Segmentos',
                href: '/segments',
                permission: permissionsTags.SEGMENTS.LIST,
              },
              {
                title: 'Atividades econômicas',
                href: '/economic-activities',
                permission: permissionsTags.ECONOMIC_ACTIVITIES.LIST,
              },
              {
                title: 'Tamanho de empresas',
                href: '/company-sizes',
                permission: permissionsTags.COMPANY_SIZES.LIST,
              },
            ],
          },
        ],
      },
      {
        items: [
          {
            main: true,
            title: 'Documentos',
            icon: ArchiveIcon,
            href: routes.regulations.all,
            items: [
              {
                title: 'Regulamentos',
                href: routes.regulations.all,
              },
            ],
          },
        ],
      },
      {
        items: [
          {
            somePermissions: [
              permissionsTags.LAST_UPDATES.LIST,
              permissionsTags.LAST_UPDATES.SHOW,
              permissionsTags.LAST_UPDATES.CREATE,
              permissionsTags.LAST_UPDATES.EDIT,
              permissionsTags.LAST_UPDATES.DESTROY,
            ],
            main: true,
            title: 'Atualizações',
            icon: UploadIcon,
            href: '/last-updates',
          },
        ],
      },
      {
        items: [
          {
            somePermissions: [
              permissionsTags.PRIVACY_POLICY.LIST,
              permissionsTags.SSO_CORPORATE_GROUP.LIST,
              permissionsTags.SSO_CORPORATE_GROUP.SHOW,
              permissionsTags.SSO_CORPORATE_GROUP.CREATE,
              permissionsTags.SSO_CORPORATE_GROUP.EDIT,
              permissionsTags.SSO_CORPORATE_GROUP.DESTROY,
            ],
            main: true,
            title: 'Configurações',
            icon: SettingsIcon,
            items: [
              {
                title: 'Políticas de privacidade',
                permission: permissionsTags.PRIVACY_POLICY.LIST,
                href: routes.privacyPolicy.all,
              },
              {
                title: 'Grupos Empresariais com Login SSO SAML',
                somePermissions: [
                  permissionsTags.SSO_CORPORATE_GROUP.LIST,
                  permissionsTags.SSO_CORPORATE_GROUP.SHOW,
                  permissionsTags.SSO_CORPORATE_GROUP.CREATE,
                  permissionsTags.SSO_CORPORATE_GROUP.EDIT,
                  permissionsTags.SSO_CORPORATE_GROUP.DESTROY,
                ],
                href: routes.ssoCorporateGroups.all,
              },
            ],
          },
        ],
      },
      {
        items: [
          {
            main: true,
            title: 'Apoio Técnico',
            icon: FeatherIcon,
            items: [
              {
                title: 'Base de conhecimento',
                href: routes.technicalFaqs.knowledgeBase,
              },
              {
                title: 'Subcategorias',
                href: routes.technicalFaqs.subcategory,
              },
            ],
          },
        ],
      },
      {
        items: [
          {
            somePermissions: [permissionsTags.FAQS_DOCUMENTATIONS.LIST],
            main: true,
            title: 'Central de Ajuda',
            icon: HelpIcon,
            items: [
              {
                title: 'FAQs',
                href: routes.faqs.all,
              },
              {
                title: 'Documentação',
                href: routes.documentations.all,
              },
            ],
          },
        ],
      },
      {
        somePermissions: [
          permissionsTags.GOVERNANCE_PROJECT.LIST,
          permissionsTags.GOVERNANCE_PROJECT.DAI_DISPUTE_ANALYSIS,
        ],
        items: [
          {
            main: true,
            title: 'Projetos de Governança',
            icon: BarChartIcon,
            href: routes.governanceProjects.all,
            somePermissions: [
              permissionsTags.GOVERNANCE_PROJECT.LIST,
              permissionsTags.GOVERNANCE_PROJECT.DAI_DISPUTE_ANALYSIS,
            ],
          },
        ],
      },
      {
        somePermissions: [
          permissionsTags.PLANS.LIST,
          permissionsTags.INTEGRATION_PARTNERS.LIST,
        ],
        items: [
          {
            main: true,
            title: 'Parceiros e Integrações',
            icon: CodeIcon,
            items: [
              {
                title: 'Planos',
                somePermissions: [permissionsTags.PLANS.LIST],
                href: routes.integrationPlans.all,
              },
              {
                title: 'Parceiros',
                somePermissions: [permissionsTags.INTEGRATION_PARTNERS.LIST],
                href: routes.integrationPartner.all,
              },
            ],
          },
        ],
      },
    ]

    setMenuItems(menuItems)
  }

  const loadUser = async () => {
    let response = await service.dponet.user.me()
    setUser(response?.data?.user)
    return response?.data?.user
  }

  const loadPermissions = async () => {
    const {
      data: { profilePermissions },
    } = await service.backoffice.users.permissions()

    setPermissions(profilePermissions)
    loadHomePage(profilePermissions)
    return profilePermissions
  }

  const loadHomePage = (permissions) => {
    const homePage = helpers.auth.getHomePageByPermission(permissions)
    setHomePage(homePage)
  }

  const loadOut = () => {
    setLoaded(true)
    setIsLoading(false)
    setMenuItems([])
    setUser(null)
    setPermissions(null)
    setHomePage()
    setAuth(false)
    queryClient.invalidateQueries()
  }

  const loadDataWithAnimation = async () => {
    setIsLoading(true)
    setLoaded(false)
    try {
      await loadData()
    } finally {
      setLoaded(true)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (service.dponet.auth.isAuthenticated()) {
      loadDataWithAnimation()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <AuthContext.Provider
      value={{
        auth,
        setAuth,
        user,
        permissions,
        setUser,
        loaded,
        isLoading,
        menuItems,
        loadDataWithAnimation,
        loadData,
        loadUser,
        homePage,
        loadOut,
        loadPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
